"use client";

import { motion, AnimatePresence } from "framer-motion";
import { X, Star, Send } from "lucide-react";
import { useState, useEffect } from "react";
import { createFeedbackApi } from "../../services/feedbackApi";
import { fetchCustomModels } from "../../services/modelApi";
import { SearchableDropdown } from "./SearchableDropdown";
import { toast } from "react-toastify";

const FeedbackModal = ({ isOpen, onClose, conversationId, modelHandle }) => {
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState(0);
  const [feedback, setFeedback] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [feedbackType, setFeedbackType] = useState("general");
  const [models, setModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(null);
  const [loadingModels, setLoadingModels] = useState(false);
  
  // Validation states
  const [errors, setErrors] = useState({
    rating: "",
    feedbackType: "",
    selectedModel: "",
    feedback: ""
  });
  const [touched, setTouched] = useState({
    rating: false,
    feedbackType: false,
    selectedModel: false,
    feedback: false
  });

  // Fetch models when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchModels();
    }
  }, [isOpen]);

  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setRating(0);
      setFeedback("");
      setFeedbackType("general");
      setSelectedModel(null);
      setErrors({
        rating: "",
        feedbackType: "",
        selectedModel: "",
        feedback: ""
      });
      setTouched({
        rating: false,
        feedbackType: false,
        selectedModel: false,
        feedback: false
      });
    }
  }, [isOpen]);

  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const response = await fetchCustomModels();
      console.log("Models response:", response);
      console.log("Models data:", response.data);
      // Format models to match SearchableDropdown expected structure
      const formattedModels = (response.data || []).map((model) => ({
        id: model._id,
        label: model.name,
        value: model.modelHandle,
      }));
      setModels(formattedModels);
    } catch (error) {
      console.error("Failed to fetch models:", error);
    } finally {
      setLoadingModels(false);
    }
  };

  // Validation functions
  const validateRating = (value) => {
    if (!value || value === 0) {
      return "Please provide a rating";
    }
    return "";
  };

  const validateFeedbackType = (value) => {
    if (!value || value.trim() === "") {
      return "Please select a feedback type";
    }
    return "";
  };

  const validateSelectedModel = (value, type) => {
    if (type === "model" && (!value || !value.value)) {
      return "Please select a bot for bot performance feedback";
    }
    return "";
  };

  const validateFeedback = (value) => {
    if (!value || value.trim() === "") {
      return "Please provide your feedback";
    }
    if (value.trim().length < 10) {
      return "Feedback must be at least 10 characters long";
    }
    if (value.length > 1000) {
      return "Feedback must not exceed 1000 characters";
    }
    return "";
  };

  // Real-time validation
  const validateField = (fieldName, value) => {
    let error = "";
    switch (fieldName) {
      case "rating":
        error = validateRating(value);
        break;
      case "feedbackType":
        error = validateFeedbackType(value);
        break;
      case "selectedModel":
        error = validateSelectedModel(value, feedbackType);
        break;
      case "feedback":
        error = validateFeedback(value);
        break;
      default:
        break;
    }
    
    setErrors(prev => ({
      ...prev,
      [fieldName]: error
    }));
    
    return error === "";
  };

  // Handle field blur (mark as touched)
  const handleBlur = (fieldName) => {
    setTouched(prev => ({
      ...prev,
      [fieldName]: true
    }));
  };

  // Handle rating change with validation
  const handleRatingChange = (value) => {
    setRating(value);
    handleBlur("rating");
    validateField("rating", value);
  };

  // Handle feedback type change with validation
  const handleFeedbackTypeChange = (value) => {
    setFeedbackType(value);
    handleBlur("feedbackType");
    validateField("feedbackType", value);
    
    // Reset model selection when changing away from model feedback
    if (value !== "model") {
      setSelectedModel(null);
      setErrors(prev => ({ ...prev, selectedModel: "" }));
      setTouched(prev => ({ ...prev, selectedModel: false }));
    } else {
      // Validate model selection when switching to model feedback
      setTimeout(() => {
        validateField("selectedModel", selectedModel);
      }, 0);
    }
  };

  // Handle model selection change with validation
  const handleModelChange = (value) => {
    setSelectedModel(value);
    handleBlur("selectedModel");
    validateField("selectedModel", value);
  };

  // Handle feedback text change with validation
  const handleFeedbackChange = (value) => {
    setFeedback(value);
    if (touched.feedback) {
      validateField("feedback", value);
    }
  };

  // Validate entire form
  const validateForm = () => {
    const ratingValid = validateField("rating", rating);
    const feedbackTypeValid = validateField("feedbackType", feedbackType);
    const modelValid = validateField("selectedModel", selectedModel);
    const feedbackValid = validateField("feedback", feedback);
    
    // Mark all fields as touched
    setTouched({
      rating: true,
      feedbackType: true,
      selectedModel: true,
      feedback: true
    });
    
    return ratingValid && feedbackTypeValid && modelValid && feedbackValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      await createFeedbackApi({
        rating,
        feedback,
        feedbackType,
        conversationId: conversationId?._id,
        modelHandle:
          feedbackType === "model" ? selectedModel?.value : modelHandle,
        timestamp: new Date().toISOString(),
      });
      toast.success("Feedback submitted successfully.");
      onClose();
    } catch (error) {
      console.error("Failed to submit feedback:", error);
      toast.error("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper component for error messages
  const ErrorMessage = ({ error, show }) => {
    if (!show || !error) return null;
    
    return (
      <p
    
        className="text-red-500 text-sm mt-1 flex items-start gap-1"
      >
        <span className="text-red-500">⚠</span>
        {error}
      </p>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-semibold text-gray-800">
                Give Feedback
              </h2>
              <button
                onClick={onClose}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Rating */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Rate your experience <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => handleRatingChange(star)}
                      onMouseEnter={() => setHoverRating(star)}
                      onMouseLeave={() => setHoverRating(0)}
                      className="p-1 hover:scale-110 transition-transform"
                    >
                      <Star
                        size={24}
                        className={`${
                          star <= (hoverRating || rating)
                            ? "fill-yellow-400 text-yellow-400"
                            : "text-gray-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
                <ErrorMessage error={errors.rating} show={touched.rating} />
              </div>

              {/* Feedback Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Feedback Type <span className="text-red-500">*</span>
                </label>
                <select
                  value={feedbackType}
                  onChange={(e) => handleFeedbackTypeChange(e.target.value)}
                  onBlur={() => handleBlur("feedbackType")}
                  className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                    errors.feedbackType && touched.feedbackType
                      ? "border-red-500"
                      : "border-gray-300"
                  }`}
                >
                  <option value="">Select feedback type</option>
                  <option value="general">General Feedback</option>
                  <option value="model">Bot Performance</option>
                  <option value="ui">User Interface</option>
                  <option value="bug">Bug Report</option>
                  <option value="feature">Feature Request</option>
                </select>
                <ErrorMessage error={errors.feedbackType} show={touched.feedbackType} />
              </div>

              {/* Model Selection - Only show when feedback type is "model" */}
              {feedbackType === "model" && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Select Bot <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={models}
                    value={selectedModel}
                    onChange={handleModelChange}
                    onBlur={() => handleBlur("selectedModel")}
                    loading={loadingModels}
                    placeholder="Select a bot to provide feedback on..."
                    searchPlaceholder="Search bots..."
                    clearable={true}
                    showAddButton={false}
                    className={`border rounded-lg ${
                      errors.selectedModel && touched.selectedModel
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    valueClassName={`p-3 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent ${
                      errors.selectedModel && touched.selectedModel
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                  />
                  <ErrorMessage error={errors.selectedModel} show={touched.selectedModel} />
                </div>
              )}

              {/* Feedback Text */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Your Feedback <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <textarea
                    value={feedback}
                    onChange={(e) => handleFeedbackChange(e.target.value)}
                    onBlur={() => handleBlur("feedback")}
                    placeholder="Share your thoughts, suggestions, or report issues..."
                    className={`w-full p-3 border rounded-lg focus:ring-2 focus:ring-primary  resize-none ${
                      errors.feedback && touched.feedback
                        ? "border-red-500"
                        : "border-gray-300"
                    }`}
                    rows={4}
                    maxLength={1000}
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-gray-400">
                    {feedback.length}/1000
                  </div>
                </div>
                <ErrorMessage error={errors.feedback} show={touched.feedback} />
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3 bg-gradient-to-r from-primary to-secondary text-white font-medium rounded-lg hover:from-primary/90 hover:to-secondary/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-all"
              >
                {isSubmitting ? (
                  "Submitting..."
                ) : (
                  <>
                    <Send size={16} />
                    Submit Feedback
                  </>
                )}
              </button>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FeedbackModal;